import type { CollectibleElastic } from "@/services/collectibles/elastic";
import type { CollectionItemId } from "@/services/collection/item";
import type { MarketplaceItem, MarketplaceItem_Api_CreationData, MarketplaceItem_Api_UpdateData, MarketplaceItem_CreatedFromCollectible, MarketplaceItem_FormModel } from "@/services/marketplace/item/marketplaceItemTypes";
import type { ShippingMethodId } from "@/services/marketplace/shippingMethod";
import { CurrencyId } from "@/services/currencies/currenciesTypes";
import type { PriceNumeric } from "@/services/prices/priceTypes";

export const assembleApiCreationData = (data: MarketplaceItem_FormModel, collectionItemId: CollectionItemId, shippingSetup: Iterable<[ShippingMethodId, number]>): MarketplaceItem_Api_CreationData => {
  if (data.quantity === undefined) {
    throw new Error("Quantity is undefined. It shouldn't be possible to create marketplace item.");
  }
  if (data.price === undefined) {
    throw new Error("Price is undefined. It shouldn't be possible to create marketplace item.");
  }

  return {
    collectionItemId,
    quantity: data.quantity,
    note: data.description ?? "",
    itemPrice: {
      priceAmountCents: data.price.price * 100,
      priceCurrency: data.price.currencyId,
    },
    itemDelivery: Array.from(shippingSetup, ([deliveryId, numberPerDelivery]) => ({
      delivery: deliveryId,
      numberPerDelivery,
    })),
  };
};

export const assembleApiUpdateData = (data: MarketplaceItem_FormModel, shippingSetup: Iterable<[ShippingMethodId, number]>, marketplaceItem: MarketplaceItem): MarketplaceItem_Api_UpdateData => {
  if (data.quantity === undefined) {
    throw new Error("Quantity is undefined. It shouldn't be possible to update marketplace item.");
  }
  if (data.price === undefined) {
    throw new Error("Price is undefined. It shouldn't be possible to update marketplace item.");
  }

  // Use ID of first already selected shipping. API is not able to add new shipping with no existing ID now.
  const itemDeliveryId = marketplaceItem.possibleShippings[0].id;

  return {
    collectionItemId: marketplaceItem.collectionItem.id,
    quantity: data.quantity,
    note: data.description ?? "",
    itemPrice: {
      id: marketplaceItem.priceId,
      priceAmountCents: data.price.price * 100,
      priceCurrency: data.price.currencyId,
    },
    itemDelivery: Array.from(shippingSetup, ([deliveryId, numberPerDelivery]) => ({
      id: itemDeliveryId,
      delivery: deliveryId,
      numberPerDelivery,
    })),
  };
};

// Approximate CZK 100 equivalent for each currency, used only to decide whether a marketplace
// listing counts as "high value" for the NewMarketplaceListingOver100Czk marketing event
// (see ColectioOrg/app.colectio.com#905). A live exchange rate isn't available at tracking
// time and isn't worth fetching just for this, so these are hardcoded, rough conversions —
// good enough for ad platform targeting.
// Keyed as Record<CurrencyId, number> (not Partial<...>) on purpose: TypeScript will fail to
// compile this object if a new CurrencyId is ever added without a matching threshold here.
const highValueListingThresholdByCurrency: Record<CurrencyId, number> = {
  [CurrencyId.Czk]: 100,
  [CurrencyId.Eur]: 4,
  [CurrencyId.Gbp]: 4,
  [CurrencyId.Usd]: 5,
};

export const isHighValueListing = (price: PriceNumeric): boolean => {
  return price.price > highValueListingThresholdByCurrency[price.currencyId];
};

export const isCreatedFromCollectible = (item: MarketplaceItem): item is MarketplaceItem_CreatedFromCollectible => {
  return item.collectible !== null;
};

export const hasCollectibleElastic = (item: MarketplaceItem): item is MarketplaceItem & { collectibleElastic: CollectibleElastic } => {
  return item.collectibleElastic !== null;
};
